/**
 * Build-time feature flags configuration
 * Controls debug features based on build context and environment
 */

// Utility function for hostname validation
const isAllowedHostname = (
  hostname: string,
  allowedHostnames: string[],
): boolean => {
  return allowedHostnames.some(
    allowed => hostname === allowed || hostname.endsWith(`.${allowed}`),
  );
};

// Build-time constants (injected during build)
export const BUILD_FLAGS = {
  // True for main/PR builds, false for tagged releases (production builds)
  ENABLE_DEBUG_FEATURES: __DEBUG_ENABLED__ || false,

  // True for development builds, false for production
  ENABLE_CONSOLE_LOGGING: __CONSOLE_LOGGING_ENABLED__ || false,

  // Build information
  BUILD_TYPE: __BUILD_TYPE__ || 'development',
  GIT_REF: __GIT_REF__ || 'unknown',
  BUILD_TIMESTAMP: __BUILD_TIMESTAMP__ || 'unknown',
  GIT_COMMIT_HASH: __GIT_COMMIT_HASH__ || 'unknown',

  // Client-side debug controls
  ENABLE_OIDC_DEBUG: __OIDC_DEBUG_ENABLED__ || false,
  ENABLE_CLIENT_DEBUG_TOOLS: __CLIENT_DEBUG_ENABLED__ || false,
} as const;

// Runtime environment detection
export const RUNTIME_FLAGS = {
  // Runtime environment checks
  get IS_LOCALHOST(): boolean {
    return (
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1')
    );
  },

  get IS_PREVIEW_ENV(): boolean {
    return (
      typeof window !== 'undefined' &&
      (window.location.hostname.startsWith('preview.') ||
        isAllowedHostname(window.location.hostname, [
          'sandbox.theinstitutes.org',
        ]))
    );
  },

  get IS_PRODUCTION_ENV(): boolean {
    return (
      typeof window !== 'undefined' &&
      window.location.hostname === 'cms.theinstitutes.org'
    );
  },

  // OIDC authentication check for preview environments
  get HAS_OIDC_AUTH(): boolean {
    if (typeof document === 'undefined') return false;
    const accessToken = document.querySelector(
      'meta[name="x-amzn-oidc-accesstoken"]',
    );
    return !!accessToken?.getAttribute('content');
  },
} as const;

// Combined feature flags logic
export const FEATURE_FLAGS = {
  // Debug endpoints should only be available in:
  // 1. Development builds (main/PR) AND
  // 2. Either localhost OR authenticated preview environments
  get ALLOW_DEBUG_ENDPOINTS(): boolean {
    if (!BUILD_FLAGS.ENABLE_DEBUG_FEATURES) return false;

    return (
      RUNTIME_FLAGS.IS_LOCALHOST ||
      (RUNTIME_FLAGS.IS_PREVIEW_ENV && RUNTIME_FLAGS.HAS_OIDC_AUTH)
    );
  },

  // Console logging allowed in:
  // 1. Development builds OR
  // 2. Localhost development
  get ALLOW_CONSOLE_DEBUG(): boolean {
    return BUILD_FLAGS.ENABLE_CONSOLE_LOGGING || RUNTIME_FLAGS.IS_LOCALHOST;
  },

  // OIDC debug features - allow in development, sandbox, and preview environments
  get ALLOW_OIDC_DEBUG(): boolean {
    return (
      BUILD_FLAGS.ENABLE_OIDC_DEBUG &&
      (RUNTIME_FLAGS.IS_LOCALHOST ||
        RUNTIME_FLAGS.IS_PREVIEW_ENV ||
        (typeof window !== 'undefined' &&
          window.location.hostname.includes('sandbox')))
    );
  },

  // Client-side debug tools (window globals, etc.)
  get ALLOW_CLIENT_DEBUG_TOOLS(): boolean {
    return (
      BUILD_FLAGS.ENABLE_CLIENT_DEBUG_TOOLS && !RUNTIME_FLAGS.IS_PRODUCTION_ENV
    );
  },

  // Debug headers exposure
  get ALLOW_DEBUG_HEADERS(): boolean {
    return (
      BUILD_FLAGS.ENABLE_DEBUG_FEATURES && !RUNTIME_FLAGS.IS_PRODUCTION_ENV
    );
  },
} as const;

// Utility functions for conditional logging
export const debugLog = (message: string, ...args: any[]) => {
  if (FEATURE_FLAGS.ALLOW_CONSOLE_DEBUG) {
    console.log(`[DEBUG] ${message}`, ...args);
  }
};

export const debugWarn = (message: string, ...args: any[]) => {
  if (FEATURE_FLAGS.ALLOW_CONSOLE_DEBUG) {
    console.warn(`[DEBUG] ${message}`, ...args);
  }
};

export const debugError = (message: string, ...args: any[]) => {
  if (FEATURE_FLAGS.ALLOW_CONSOLE_DEBUG) {
    console.error(`[DEBUG] ${message}`, ...args);
  }
};

// Runtime feature flag checker
export const isFeatureEnabled = (flag: keyof typeof FEATURE_FLAGS): boolean => {
  return FEATURE_FLAGS[flag];
};

// Build info for debugging
export const getBuildInfo = () => {
  if (!FEATURE_FLAGS.ALLOW_CONSOLE_DEBUG) return null;

  return {
    buildType: BUILD_FLAGS.BUILD_TYPE,
    gitRef: BUILD_FLAGS.GIT_REF,
    debugEnabled: BUILD_FLAGS.ENABLE_DEBUG_FEATURES,
    consoleLoggingEnabled: BUILD_FLAGS.ENABLE_CONSOLE_LOGGING,
    environment: {
      isLocalhost: RUNTIME_FLAGS.IS_LOCALHOST,
      isPreview: RUNTIME_FLAGS.IS_PREVIEW_ENV,
      isProduction: RUNTIME_FLAGS.IS_PRODUCTION_ENV,
      hasOIDCAuth: RUNTIME_FLAGS.HAS_OIDC_AUTH,
    },
    features: {
      debugEndpoints: FEATURE_FLAGS.ALLOW_DEBUG_ENDPOINTS,
      consoleDebug: FEATURE_FLAGS.ALLOW_CONSOLE_DEBUG,
      oidcDebug: FEATURE_FLAGS.ALLOW_OIDC_DEBUG,
      clientDebugTools: FEATURE_FLAGS.ALLOW_CLIENT_DEBUG_TOOLS,
      debugHeaders: FEATURE_FLAGS.ALLOW_DEBUG_HEADERS,
    },
  };
};
